// src/context/AuthContext.jsx

import React, { createContext, useContext, useEffect, useState } from "react";
import {
  GoogleAuthProvider,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  getIdTokenResult,
} from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { auth, messaging, VAPID_KEY } from "../firebase/config";
import { 
  doc, 
  getDoc, 
  setDoc, 
  addDoc, 
  collection, 
  serverTimestamp,
  query,
  where,
  getDocs
} from "firebase/firestore";
import { db, functions } from "../firebase/config";
import { getToken } from "firebase/messaging";
import { httpsCallable } from "firebase/functions";

// Create Context
const AuthContext = createContext();

// Hook
export const useAuth = () => useContext(AuthContext);

// Provider Component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);         // Firebase user
  const [role, setRole] = useState(null);         // Custom role (admin, deliveryPartner, etc)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // ✅ Check admin role from Firestore
  const checkAdminRole = async (email) => {
    try {
      const adminQuery = query(
        collection(db, "admin"),
        where("email", "==", email)
      );
      const adminSnapshot = await getDocs(adminQuery);
      
      if (!adminSnapshot.empty) {
        const adminData = adminSnapshot.docs[0].data();
        console.log("✅ Admin found in Firestore:", adminData);
        return adminData.role || "admin";
      }
      return null;
    } catch (error) {
      console.error("❌ Error checking admin role:", error);
      return null;
    }
  };

  // Create user profile if not exists
  const createUserProfile = async (firebaseUser) => {
    try {
      const userRef = doc(db, "users", firebaseUser.uid);
      const userSnap = await getDoc(userRef);

      if (!userSnap.exists()) {
        // Create user document
        await setDoc(userRef, {
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          displayName: firebaseUser.displayName || "",
          userName: firebaseUser.displayName || "",
          photoURL: firebaseUser.photoURL || "",
          createdAt: serverTimestamp(),
          role: "customer", // default role
          phone: "",
          address: "",
          addressDetails: "",
          isActive: true,
          lastLoginAt: serverTimestamp()
        });

        console.log("✅ User profile created for:", firebaseUser.email);
      } else {
        // Update last login time for existing users
        await setDoc(userRef, {
          lastLoginAt: serverTimestamp()
        }, { merge: true });
      }
    } catch (error) {
      console.error("❌ Error creating/updating user profile:", error);
      // Don't block login if profile creation fails
    }
  };

  // Save FCM token to Firestore
  const saveFcmToken = async (firebaseUser) => {
    try {
      const token = await getToken(messaging, { vapidKey: VAPID_KEY });
      if (token) {
        await setDoc(doc(db, "users", firebaseUser.uid), { fcmToken: token }, { merge: true });
        console.log("✅ FCM token saved");
      }
    } catch (error) {
      console.error("❌ Failed to save FCM token:", error);
    }
  };

  // Set default customer role if user doesn't have one
  const setDefaultCustomerRole = async (firebaseUser) => {
    try {
      const idTokenResult = await getIdTokenResult(firebaseUser);
      
      // Only set default role if user doesn't have any role
      if (!idTokenResult.claims.role) {
        console.log("🔧 Setting default customer role for:", firebaseUser.email);
        const setRole = httpsCallable(functions, "setDefaultCustomerRole");
        await setRole();
        console.log("✅ Default customer role set");
      }
    } catch (error) {
      console.error("❌ Error setting default customer role:", error);
      // Don't block login if role setting fails
    }
  };

  // Sign In with automatic redirection and referral support
  const signInWithGoogle = async (referrerId = null) => {
    const provider = new GoogleAuthProvider();
    try {
      const result = await signInWithPopup(auth, provider);
      const idTokenResult = await getIdTokenResult(result.user);

      setUser(result.user);
      
      // ✅ Check both custom claims and Firestore admin collection
      let userRole = idTokenResult.claims.role || null;
      
      // If no role in claims, check admin collection
      if (!userRole) {
        userRole = await checkAdminRole(result.user.email);
      }
      
      setRole(userRole);

      console.log("✅ Logged in:", result.user.email, "Role:", userRole);
      
      // Create user profile (if not exists)
      await createUserProfile(result.user);
      
      // Set default customer role if needed
      await setDefaultCustomerRole(result.user);
      
      // Save FCM token
      await saveFcmToken(result.user);
      
      // Handle referral if this is a new user
      if (referrerId) {
        await handleReferral(result.user, referrerId);
      }
      
      // ✅ Automatically redirect to home after successful login
      navigate("/");
      
    } catch (error) {
      console.error("❌ Google Sign-In error:", error.message);
      alert("Google Sign-In failed. Please try again.");
    }
  };

  // Handle referral logic
  const handleReferral = async (newUser, referrerId) => {
    try {
      // Create user profile if not exists
      await createUserProfile(newUser);
      
      // Check if this is a new user for referral purposes
      const userDocRef = doc(db, "users", newUser.uid);
      const userSnap = await getDoc(userDocRef);

      if (userSnap.exists()) {
        // Update user document with referral info
        await setDoc(userDocRef, {
          referredBy: referrerId
        }, { merge: true });

        // Create referral record
        const referralData = {
          referrerId,
          referredUserId: newUser.uid,
          referredUserEmail: newUser.email,
          status: "pending",
          createdAt: serverTimestamp()
        };

        await addDoc(collection(db, "referrals"), referralData);

        console.log("🎁 Referral record created for:", newUser.email);
      }
    } catch (error) {
      console.error("❌ Error handling referral:", error);
      // Don't block login if referral fails
    }
  };

  // Logout
  const logout = async () => {
    try {
      await signOut(auth);
      setUser(null);
      setRole(null);
      console.log("👋 Logged out");
    } catch (error) {
      console.error("❌ Logout error:", error.message);
      alert("Logout failed. Try again.");
    }
  };

  // ✅ Manual token refresh for testing role changes
  const refreshUserToken = async () => {
    if (!user) return;
    
    try {
      console.log("🔄 Forcing token refresh...");
      await user.getIdToken(true);
      const idTokenResult = await getIdTokenResult(user);
      
      setRole(idTokenResult.claims.role || null);
      console.log("✅ Token refreshed - Role:", idTokenResult.claims.role);
      
      return idTokenResult.claims.role;
    } catch (error) {
      console.error("❌ Token refresh error:", error);
      return null;
    }
  };

  // Auth listener
  useEffect(() => {
    let timeoutId;
    
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      try {
        if (currentUser) {
          // ✅ Force refresh token to get latest custom claims
          await currentUser.getIdToken(true);
          const idTokenResult = await getIdTokenResult(currentUser);
          
          setUser(currentUser);
          
          // ✅ Check both custom claims and Firestore admin collection
          let userRole = idTokenResult.claims.role || null;
          
          // If no role in claims, check admin collection
          if (!userRole) {
            userRole = await checkAdminRole(currentUser.email);
          }
          
          setRole(userRole);
          console.log("🔁 Auth change:", currentUser.email, "Role:", userRole);
          
          // Ensure user profile exists (for existing users who might not have profiles)
          await createUserProfile(currentUser);
          
          // Set default customer role if needed
          await setDefaultCustomerRole(currentUser);
          
          // Save FCM token on auth state change
          await saveFcmToken(currentUser);
        } else {
          setUser(null);
          setRole(null);
        }
        setError(null);
      } catch (error) {
        console.error("❌ Auth state change error:", error);
        setError(error.message);
        // Still set loading to false so app doesn't get stuck
      } finally {
        setLoading(false);
      }
    }, (error) => {
      console.error("❌ Auth state change error:", error);
      setError(error.message);
      setLoading(false);
    });

    // Fallback timeout to prevent infinite loading
    timeoutId = setTimeout(() => {
      if (loading) {
        console.warn("⚠️ Auth loading timeout - forcing app to render");
        setLoading(false);
        setError("Authentication timeout");
      }
    }, 5000); // 5 second timeout

    return () => {
      unsubscribe();
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [loading]);

  // Show loading spinner while authenticating
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-green-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-green-700 font-medium">Loading...</p>
          {error && (
            <p className="text-red-600 text-sm mt-2">Error: {error}</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ 
      user, 
      role, 
      loading, 
      signInWithGoogle, 
      logout, 
      refreshUserToken,
      error 
    }}>
      {children}
    </AuthContext.Provider>
  );
};
